import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabase';
import './PostCreationBox.css';
import '../styles/buttons.css';
import '../styles/color-scheme.css';
import { Icon } from './Icon.js';
import ProtocolSelector from './ProtocolSelector.js';
import ProtocolReportSelector from './ProtocolReportSelector.js';
import BiomarkerSelector from './BiomarkerSelector.js';
import BiohackInfoCard from './BiohackInfoCard';
import AttachmentDisplay from './AttachmentDisplay.js';
import { useUserProfile } from '../hooks/useUserProfile.js';
import { useAttachments } from '../hooks/useAttachments.js';
import { useImageUpload } from '../hooks/useImageUpload.js';
import { 
  validatePostData, 
  createPost, 
  processAttachment, 
  prepareAttachmentsForSubmission,
  createBiohackTags,
  createBiomarkerTags,
  getBiohacksFromProtocol,
  getBiohacksFromProtocolReport,
  getBiomarkersFromProtocolReport
} from '../utils/postSubmission.js';
import SearchService from '../services/SearchService.js';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';

const PostCreationBox = ({ onPostCreated, userProfile }) => {
  // 
  // Core form state
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [isVisibilityFabOpen, setIsVisibilityFabOpen] = useState(false);
  
  const fileInputRef = useRef(null);
  const postCreationRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const visibilityFabMenuRef = useRef(null);
  const visibilityFabButtonRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Custom hooks for separated concerns
  const user = useUserProfile(userProfile);
  const {
    attachments,
    attachmentOrder,
    setAttachmentOrder,
    addImages,
    addProtocol,
    addBiohacks,
    addBiomarker,
    addProtocolReport,
    removeAttachment,
    resetAttachments
  } = useAttachments();
  
  const [showProtocolSelector, setShowProtocolSelector] = useState(false);
  const [showProtocolReportSelector, setShowProtocolReportSelector] = useState(false);
  const [showBiomarkerSelector, setShowBiomarkerSelector] = useState(false);
  const { isUploading, error: uploadError, uploadImages, clearError: clearUploadError } = useImageUpload(addImages);
  
  // BiohackInfoCard modal state
  const [showBiohackInfo, setShowBiohackInfo] = useState(false);
  const [selectedBiohack, setSelectedBiohack] = useState(null);

  // Disclaimer modal state
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  // Biohack suggestions state
  const [biohackSuggestions, setBiohackSuggestions] = useState([]);
  // Biomarker tags state (separate from biomarker attachment)
  const [biomarkerTags, setBiomarkerTags] = useState([]);
  const [biomarkerSuggestions, setBiomarkerSuggestions] = useState([]);
  // Biohacks from attached protocol
  const [protocolBiohacks, setProtocolBiohacks] = useState([]);
  // Biohacks and biomarkers from attached protocol report
  const [reportBiohacks, setReportBiohacks] = useState([]);
  const [reportBiomarkers, setReportBiomarkers] = useState([]);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const titleRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const focusTitleOnExpandRef = useRef(false);
  const searchTimerRef = useRef(null);
  const biomarkerSearchTimerRef = useRef(null);

  // Check if we have any attachments (tags are not attachments)
  const hasAttachments = attachments.images.length > 0 || 
                        attachments.protocol || 
                        attachments.biomarker ||
                        attachments.protocolReport;

  // Check if we should show health data sharing warning
  const hasHealthDataAttachment = attachments.protocolReport || attachments.biomarker;
  const isPublicOrFollowers = visibility === 'public' || visibility === 'followers';
  const showHealthDataWarning = hasHealthDataAttachment && isPublicOrFollowers;

  // Protocol tab privacy state
  const [protocolTabPrivacy, setProtocolTabPrivacy] = useState(null);
  
  // Fetch protocol tab privacy setting (deduplicated to avoid duplicate request from React Strict Mode)
  useEffect(() => {
    let isMounted = true;

    const fetchProtocolTabPrivacy = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const authUser = session?.user;
        if (!authUser) return;

        const cacheKey = getCacheKey('fetchProtocolTabPrivacy', authUser.id);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: row, error } = await supabase
            .from('user_tab_privacy')
            .select('privacy_level')
            .eq('user_id', authUser.id)
            .eq('tab_name', 'protocols')
            .maybeSingle();
          if (error) throw error;
          return row?.privacy_level ?? 'public';
        });

        if (isMounted) setProtocolTabPrivacy(data ?? 'public');
      } catch (error) {
        console.error('Error fetching protocol tab privacy:', error);
        if (isMounted) setProtocolTabPrivacy('public');
      }
    };

    fetchProtocolTabPrivacy();
    return () => { isMounted = false; };
  }, []);

  // Check if there's a mismatch between protocol tab privacy and post visibility
  const hasProtocolPrivacyMismatch = attachments.protocol && protocolTabPrivacy && (
    // Mismatch: protocol tab is private/followers_only but post is public
    (protocolTabPrivacy === 'private' && visibility === 'public') ||
    (protocolTabPrivacy === 'private' && visibility === 'followers') ||
    (protocolTabPrivacy === 'followers_only' && visibility === 'public')
  );

  // Handle clicks outside the post creation box
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (postCreationRef.current && !postCreationRef.current.contains(event.target)) {
        // Only collapse if there's no title, no content, and no attachments
        const hasContent = title.trim() ||
                          content.trim() || 
                          attachments.images.length > 0 || 
                          attachments.protocol || 
                          attachments.biohacks.length > 0 ||
                          protocolBiohacks.length > 0 ||
                          reportBiohacks.length > 0 ||
                          attachments.biomarker ||
                          attachments.protocolReport ||
                          biomarkerTags.length > 0 ||
                          reportBiomarkers.length > 0;
        if (!hasContent) {
          setIsExpanded(false);
        }
      }
      
      // Close visibility FAB menu when clicking outside
      if (
        isVisibilityFabOpen &&
        visibilityFabMenuRef.current &&
        !visibilityFabMenuRef.current.contains(event.target) &&
        visibilityFabButtonRef.current &&
        !visibilityFabButtonRef.current.contains(event.target)
      ) {
        setIsVisibilityFabOpen(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded, title, content, attachments, protocolBiohacks, reportBiohacks, biomarkerTags, reportBiomarkers, isVisibilityFabOpen]);
  
  // Focus title when expanding from collapsed input (click or type)
  useEffect(() => {
    if (!isExpanded || !focusTitleOnExpandRef.current) return;
    focusTitleOnExpandRef.current = false;
    const id = setTimeout(() => {
      if (titleRef.current) titleRef.current.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isExpanded]);

  // Close visibility FAB menu on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisibilityFabOpen) {
        setIsVisibilityFabOpen(false);
      }
    };

    if (isVisibilityFabOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isVisibilityFabOpen]);

  // Consolidated error handling
  const displayError = error || uploadError;

  // Handle content changes (content is optional; only used in expanded textarea)
  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);
    if (displayError) {
      setError('');
      clearUploadError();
    }
  };
  
  // Handle keyboard events for suggestion completion
  const handleKeyDown = (e) => {
    // Tab key to accept first suggestion (prioritize biohack over biomarker)
    if (e.key === 'Tab' && biohackSuggestions.length > 0) {
      e.preventDefault();
      addBiohackTag(biohackSuggestions[0]);
    } else if (e.key === 'Tab' && biomarkerSuggestions.length > 0) {
      e.preventDefault();
      addBiomarkerTag(biomarkerSuggestions[0]);
    }
    
    // Escape to clear suggestions
    if (e.key === 'Escape') {
      setBiohackSuggestions([]);
      setBiomarkerSuggestions([]);
    }
  };

  // Search for biohack suggestions as user types
  useEffect(() => {
    if (!isExpanded || !content.trim()) {
      setBiohackSuggestions([]);
      return;
    }

    // Don't show suggestions if content ends with a space (user just finished a word)
    if (content.endsWith(' ')) {
      setBiohackSuggestions([]);
      return;
    }

    // Get the last word being typed
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1]?.toLowerCase().trim() || '';

    // Need at least 2 characters to search
    if (lastWord.length < 2) {
      setBiohackSuggestions([]);
      return;
    }

    // Debounce the search
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await SearchService.searchBiohacksByName(lastWord, 5);
        
        // Filter out already selected biohacks
        const selectedIds = new Set((attachments.biohacks || []).map(b => b.id));
        const filteredResults = (results || []).filter(b => !selectedIds.has(b.id));
        
        setBiohackSuggestions(filteredResults);
        // Clear biomarker suggestions when biohack suggestions are set (biohack takes priority)
        if (filteredResults.length > 0) {
          setBiomarkerSuggestions([]);
        }
      } catch (error) {
        console.error('Error searching biohacks:', error);
        setBiohackSuggestions([]);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [content, isExpanded, attachments.biohacks]);

  // Search for biomarker suggestions as user types
  useEffect(() => {
    if (!isExpanded || !content.trim()) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Don't show suggestions if content ends with a space (user just finished a word)
    if (content.endsWith(' ')) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Get the last word being typed
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1]?.toLowerCase().trim() || '';

    // Need at least 2 characters to search
    if (lastWord.length < 2) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Only show biomarker suggestions if there are no biohack suggestions (biohack takes priority)
    // Check biohackSuggestions state directly to avoid unnecessary re-runs
    if (biohackSuggestions.length > 0) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Debounce the search
    if (biomarkerSearchTimerRef.current) {
      clearTimeout(biomarkerSearchTimerRef.current);
    }

    biomarkerSearchTimerRef.current = setTimeout(async () => {
      // Double-check biohack suggestions haven't appeared during debounce
      // This prevents race conditions where biohack search completes after biomarker search starts
      if (biohackSuggestions.length > 0) {
        setBiomarkerSuggestions([]);
        return;
      }

      try {
        const results = await SearchService.searchBiomarkersByName(lastWord, 5);
        
        // Filter out already selected biomarkers
        const selectedIds = new Set(biomarkerTags.map(b => b.id));
        const filteredResults = (results || []).filter(b => !selectedIds.has(b.id));
        
        // Set biomarker suggestions (they will be cleared if biohack suggestions exist due to the check above)
        setBiomarkerSuggestions(filteredResults);
      } catch (error) {
        console.error('Error searching biomarkers:', error);
        setBiomarkerSuggestions([]);
      }
    }, 350); // Slightly longer delay than biohack (300ms) to ensure biohack completes first

    return () => {
      if (biomarkerSearchTimerRef.current) {
        clearTimeout(biomarkerSearchTimerRef.current);
      }
    };
  }, [content, isExpanded, biomarkerTags, biohackSuggestions]);

  // Extract biohacks from attached protocol
  useEffect(() => {
    const extractProtocolBiohacks = async () => {
      if (attachments.protocol) {
        const biohacks = await getBiohacksFromProtocol(attachments.protocol);
        setProtocolBiohacks(biohacks || []);
      } else {
        setProtocolBiohacks([]);
      }
    };

    extractProtocolBiohacks();
  }, [attachments.protocol]);

  // Extract biohacks and biomarkers from attached protocol report
  useEffect(() => {
    const extractReportBiotags = async () => {
      if (attachments.protocolReport) {
        const [biohacks, biomarkers] = await Promise.all([
          getBiohacksFromProtocolReport(attachments.protocolReport),
          getBiomarkersFromProtocolReport(attachments.protocolReport)
        ]);
        setReportBiohacks(biohacks || []);
        setReportBiomarkers((biomarkers || []).filter(b => b && b.name));
      } else {
        setReportBiohacks([]);
        setReportBiomarkers([]);
      }
    };

    extractReportBiotags();
  }, [attachments.protocolReport]);

  // Remove biotags when their text is deleted from content
  useEffect(() => {
    if (!content || !isExpanded) return;

    const contentLower = content.toLowerCase();
    // Split content into words, removing punctuation for matching
    const words = contentLower.split(/\s+/).map(word => word.replace(/[.,;:!?()[\]{}"'`]/g, ''));

    // Check manually created biohack tags (not protocol biohacks)
    if (attachments.biohacks && attachments.biohacks.length > 0) {
      const biohacksToKeep = attachments.biohacks.filter(biohack => {
        const biohackNameLower = biohack.name.toLowerCase();
        // Check if the biohack name appears as a complete word/phrase in the content
        // Split the biohack name into words for multi-word matching
        const biohackWords = biohackNameLower.split(/\s+/);
        
        // For single-word biohacks, check exact word match
        if (biohackWords.length === 1) {
          return words.includes(biohackWords[0]);
        }
        
        // For multi-word biohacks, check if all words appear consecutively in content
        // This handles cases like "Vitamin D" where we want to match the full phrase
        const biohackPhrase = biohackWords.join(' ');
        return contentLower.includes(biohackPhrase);
      });

      // Only update if there's a difference to avoid unnecessary re-renders
      if (biohacksToKeep.length !== attachments.biohacks.length) {
        addBiohacks(biohacksToKeep);
      }
    }

    // Check biomarker tags
    // Don't remove biomarker tags that come from the biomarker attachment or report (similar to protocol biohacks)
    if (biomarkerTags && biomarkerTags.length > 0) {
      const biomarkersToKeep = biomarkerTags.filter(biomarker => {
        if (!biomarker || !biomarker.name) return false;
        // If this biomarker comes from the biomarker attachment, always keep it
        if (attachments.biomarker && attachments.biomarker.id === biomarker.id) {
          return true;
        }
        
        // If this biomarker comes from the report, always keep it
        if (reportBiomarkers.some(rb => rb && rb.id === biomarker.id)) {
          return true;
        }
        
        // For manually typed biomarker tags, check if the name is still in content
        const biomarkerNameLower = biomarker.name.toLowerCase();
        // Split the biomarker name into words for multi-word matching
        const biomarkerWords = biomarkerNameLower.split(/\s+/);
        
        // For single-word biomarkers, check exact word match
        if (biomarkerWords.length === 1) {
          return words.includes(biomarkerWords[0]);
        }
        
        // For multi-word biomarkers, check if all words appear consecutively in content
        const biomarkerPhrase = biomarkerWords.join(' ');
        return contentLower.includes(biomarkerPhrase);
      });

      // Only update if there's a difference to avoid unnecessary re-renders
      if (biomarkersToKeep.length !== biomarkerTags.length) {
        setBiomarkerTags(biomarkersToKeep);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isExpanded]);

  // Add biohack tag when suggestion is clicked or Tab is pressed
  const addBiohackTag = (biohack) => {
    // Check if already tagged (either manually, from protocol, or from report)
    const alreadyTaggedManually = (attachments.biohacks || []).some(b => b && b.id === biohack.id);
    const alreadyTaggedFromProtocol = (protocolBiohacks || []).some(b => b && b.id === biohack.id);
    const alreadyTaggedFromReport = (reportBiohacks || []).some(b => b && b.id === biohack.id);
    if (alreadyTaggedManually || alreadyTaggedFromProtocol || alreadyTaggedFromReport) {
      setBiohackSuggestions([]);
      return;
    }

    // Replace the last word in content with the full biohack name
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1] || '';
    
    // Only replace if there's actually a word being typed
    if (lastWord) {
      // Remove the last word and add the biohack name
      const newWords = words.slice(0, -1);
      newWords.push(biohack.name);
      setContent(newWords.join(' ') + ' ');
    }

    // Add to biohack attachments
    addBiohacks([...(attachments.biohacks || []), biohack]);
    
    // Clear suggestions
    setBiohackSuggestions([]);
    
    // Focus back on the input
    const element = textareaRef.current || inputRef.current;
    if (element) {
      setTimeout(() => element.focus(), 0);
    }
  };

  // Add biomarker tag when suggestion is clicked or Tab is pressed
  const addBiomarkerTag = (biomarker) => {
    // Check if already tagged (either manually, from biomarker attachment, or from report)
    const alreadyTaggedManually = biomarkerTags.some(b => b && b.id === biomarker.id);
    const alreadyTaggedFromAttachment = attachments.biomarker && attachments.biomarker.id === biomarker.id;
    const alreadyTaggedFromReport = (reportBiomarkers || []).some(b => b && b.id === biomarker.id);
    if (alreadyTaggedManually || alreadyTaggedFromAttachment || alreadyTaggedFromReport) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Replace the last word in content with the full biomarker name
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1] || '';
    
    // Only replace if there's actually a word being typed
    if (lastWord) {
      // Remove the last word and add the biomarker name
      const newWords = words.slice(0, -1);
      newWords.push(biomarker.name);
      setContent(newWords.join(' ') + ' ');
    }

    // Add to biomarker tags
    setBiomarkerTags([...biomarkerTags, biomarker]);
    
    // Clear suggestions
    setBiomarkerSuggestions([]);
    
    // Focus back on the input
    const element = textareaRef.current || inputRef.current;
    if (element) {
      setTimeout(() => element.focus(), 0);
    }
  };

  // Wrapper function to handle biomarker attachment removal with tag removal
  const handleRemoveAttachment = (type, id) => {
    // If removing biomarker attachment, also remove from biomarkerTags
    if (type === 'biomarker' && attachments.biomarker) {
      setBiomarkerTags(biomarkerTags.filter(b => b && b.id !== attachments.biomarker.id));
    }
    removeAttachment(type, id);
  };

  // Handle title changes
  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    if (value.trim() && !isExpanded) {
      setIsExpanded(true);
      focusTitleOnExpandRef.current = true;
    }
    if (displayError) {
      setError('');
      clearUploadError();
    }
  };

  // Handle focus on collapsed input (title) - expand and focus title when expanded
  const handleCollapsedFocus = () => {
    focusTitleOnExpandRef.current = true;
    setIsExpanded(true);
  };

  // Handle focus - expand when user clicks on content textarea (e.g. when already expanded)
  const handleFocus = () => {
    setIsExpanded(true);
  };

  // Handle blur - simplified since we use click outside detection
  const handleBlur = (e) => {
    // Only handle blur for the textarea/content input
    // The click outside handler will manage collapsing
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // If a protocol report is selected, don't allow adding images
    if (attachments.protocolReport) {
      setError('Remove the report to add images');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    await uploadImages(files, attachments.images.length);
    setIsExpanded(true);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle attachment selectors
  const handleProtocolButtonClick = () => {
    setShowProtocolSelector(true);
    setIsExpanded(true);
  };

  // Removed biohack attachment button/modal per updated requirements

  const handleBiomarkerButtonClick = () => {
    setShowBiomarkerSelector(true);
    setIsExpanded(true);
  };

  const handleProtocolSelect = (protocol) => {
    // If a protocol report is selected, don't allow adding other attachments
    if (attachments.protocolReport) {
      setError('Remove the report to add other attachments');
      return;
    }
    addProtocol(protocol);
  };

  const handleProtocolReportSelect = (protocolReport) => {
    // Check if there are other attachments
    if (attachments.images.length > 0 || attachments.protocol || attachments.biomarker) {
      setError('Reports can only be attached alone. Remove other attachments first.');
      return;
    }
    addProtocolReport(protocolReport);
  };

  // Biohack selector removed; suggestions add tags directly

  const handleBiomarkerSelect = (biomarker) => {
    // Deselect: remove biomarker attachment and clear related tags
    if (!biomarker) {
      if (attachments.biomarker) {
        const idToRemove = attachments.biomarker.id;
        removeAttachment('biomarker', idToRemove);
        setBiomarkerTags(prev => prev.filter(b => b && b.id !== idToRemove));
      }
      return;
    }

    // If a protocol report is selected, don't allow adding other attachments
    if (attachments.protocolReport) {
      setError('Remove the report to add other attachments');
      return;
    }
    
    // Remove old biomarker tag if there was a previous biomarker attachment
    let updatedTags = [...biomarkerTags].filter(Boolean);
    if (attachments.biomarker) {
      updatedTags = updatedTags.filter(b => b.id !== attachments.biomarker.id);
    }
    
    // Add the new biomarker to the attachment
    addBiomarker(biomarker);
    
    // Automatically add biomarker to tags (if not already tagged)
    const alreadyTagged = updatedTags.some(b => b.id === biomarker.id);
    if (!alreadyTagged) {
      updatedTags.push(biomarker);
    }
    setBiomarkerTags(updatedTags);
  };

  const handleProtocolDone = () => {
    setShowProtocolSelector(false);
  };

  const handleProtocolReportDone = () => {
    setShowProtocolReportSelector(false);
  };

  // Biohack selector removed

  const handleBiomarkerDone = () => {
    setShowBiomarkerSelector(false);
  };

  // Check if user has accepted the post creation disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'home_feed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'home_feed')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'home_feed',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Internal function to perform post submission (after disclaimer check)
  const submitPostInternal = async () => {
    try {
      validatePostData(content, title);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const authUser = session?.user;
      if (!authUser) throw new Error('You must be signed in to post.');

      // Check user exists in user_profiles table
      const { data: userRow, error: userRowError } = await supabase
        .from('user_profiles')
        .select('user_id')
        .eq('user_id', authUser.id)
        .maybeSingle();
        
      if (userRowError || !userRow) {
        throw new Error('User not found. Please sign out and sign in again.');
      }

      // Create post
      const postData = {
        user_id: authUser.id,
        content: content.trim(),
        title: title.trim() || null,
        visibility: visibility,
        has_attachment: attachments.images.length > 0 || 
                       !!attachments.protocol || 
                       attachments.biohacks.length > 0 ||
                       !!attachments.biomarker ||
                       !!attachments.protocolReport
      };

      const postId = await createPost(postData);

      // Handle attachments
      const allAttachmentsForSubmission = prepareAttachmentsForSubmission(attachments);
      
      // Process attachments
      for (const attachment of allAttachmentsForSubmission) {
        await processAttachment(postId, attachment);
      }

      // Collect all biohacks for tagging
      const allBiohacks = [];

      // Add manually entered biohacks
      if (attachments.biohacks && attachments.biohacks.length > 0) {
        allBiohacks.push(...attachments.biohacks);
      }

      // Add biohacks from attached protocol (already extracted in state)
      if (protocolBiohacks && protocolBiohacks.length > 0) {
        allBiohacks.push(...protocolBiohacks);
      }

      // Add biohacks from attached protocol report (already extracted in state)
      if (reportBiohacks && reportBiohacks.length > 0) {
        allBiohacks.push(...reportBiohacks);
      }

      // Create biohack tags for all collected biohacks
      if (allBiohacks.length > 0) {
        await createBiohackTags(postId, allBiohacks);
      }

      // Collect all biomarkers for tagging
      const allBiomarkers = [];

      // Add manually entered biomarker tags
      if (biomarkerTags && biomarkerTags.length > 0) {
        allBiomarkers.push(...biomarkerTags);
      }

      // Add biomarkers from attached protocol report (already extracted in state)
      if (reportBiomarkers && reportBiomarkers.length > 0) {
        allBiomarkers.push(...reportBiomarkers);
      }

      // Create biomarker tags for all collected biomarkers
      if (allBiomarkers.length > 0) {
        await createBiomarkerTags(postId, allBiomarkers);
      }

      // Fetch the created post with all its relationships to add to feed
      const { data: createdPost, error: fetchError } = await supabase
        .from('posts')
        .select(`
          id, user_id, title, content, created_at, updated_at, visibility, is_deleted, shared_from, reposts_count, likes_count,
          user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url, user_id, gender),
          post_attachments:post_attachments_post_id_fkey(
            id, type, sort_order,
            post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
              protocols:protocol_id(
                id, name, description, pop_score, adherence_score, created_at,
                protocol_hacks:protocol_hacks_protocol_id_fkey(
                  id, position,
                  biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
                ),
                user_profiles!protocols_created_by_fkey(user_id, username)
              )
            ),
            post_attachment_images:post_attachment_images_post_attachment_id_fkey(
              id, image_url, file_name, mime_type
            ),
            post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
              protocol_report_snapshots:report_id(
                id, protocol_id, title, snapshot_date, created_at
              )
            )
          ),
          post_biohack_tags:post_biohack_tags_post_id_fkey(
            biohacks:biohack_id(id, name, image_url, category)
          ),
          post_biomarker_tags:post_biomarker_tags_post_id_fkey(
            biomarkers:biomarker_id(id, name, category, unit)
          )
        `)
        .eq('id', postId)
        .single();

      if (fetchError) {
        console.error('Error fetching created post:', fetchError);
      }

      // Reset form
      setContent('');
      setTitle('');
      setVisibility('public');
      resetAttachments();
      setBiomarkerTags([]);
      setReportBiohacks([]);
      setReportBiomarkers([]);
      setIsExpanded(false);
      setIsVisibilityFabOpen(false);
      
      if (onPostCreated) onPostCreated(createdPost || null);

    } catch (error) {
      console.error('Post creation error:', error);
      setError(getUserFacingErrorMessage(error, 'Failed to post. Please try again.'));
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  // Handle post submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent double submit: guard synchronously so a second click does nothing
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const authUser = session?.user;
      if (!authUser) throw new Error('You must be signed in to post.');

      // Check if user has accepted the disclaimer
      const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(authUser.id);
      
      if (!hasAcceptedDisclaimer) {
        // Fetch disclaimer text and show modal
        const disclaimer = await fetchDisclaimerText();
        if (disclaimer && disclaimer.text) {
          setDisclaimerText(disclaimer.text);
          setDisclaimerVersion(disclaimer.version || 'v1.0');
          setShowDisclaimerModal(true);
          setAcceptResponsibility(false);
          setDoNotShowAgain(false);
          isSubmittingRef.current = false;
          setIsLoading(false);
          return;
        }
      }

      // User has accepted disclaimer, proceed with posting
      await submitPostInternal();
    } catch (error) {
      console.error('Error submitting post:', error);
      setError(getUserFacingErrorMessage(error, 'Failed to submit post. Please try again.'));
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const authUser = session?.user;
      if (!authUser) {
        setShowDisclaimerModal(false);
        isSubmittingRef.current = false;
        setIsLoading(false);
        return;
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(authUser.id, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // User has accepted disclaimer, proceed with posting
        await submitPostInternal();
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
        isSubmittingRef.current = false;
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
  };

  const currentLoading = isLoading || isUploading;

  return (
    <div className={`post-creation-box ${isExpanded ? 'is-expanded' : ''}`} ref={postCreationRef}>
      <form onSubmit={handleSubmit}>
        <div className="post-create-card">
          <div className="post-create-header">
            <div className="post-create-avatar">
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt={`${user.username}'s profile`} />
              ) : user.username ? (
                <div className="avatar-fallback">
                  {user.username.charAt(0).toUpperCase()}
                </div>
              ) : (
                <div className="avatar-loading">...</div>
              )}
            </div>
            
            <div className="post-create-main">
              {!isExpanded ? (
                <>
                  <div className="post-create-input-wrapper" ref={inputWrapperRef}>
                    <input
                      className="post-create-input"
                      placeholder={`What did you discover today${user.username ? ' ' + user.username : ''}?`}
                      value={title}
                      onChange={handleTitleChange}
                      ref={inputRef}
                      onFocus={handleCollapsedFocus}
                      disabled={currentLoading}
                      maxLength={500}
                      autoComplete="off"
                    />
                  </div>
                </>
              ) : (
                <div className="post-create-expanded">
                  <input
                    ref={titleRef}
                    className="post-create-title"
                    placeholder={`What did you discover today${user.username ? ' ' + user.username : ''}?`}
                    value={title}
                    onChange={handleTitleChange}
                    onBlur={handleBlur}
                    disabled={currentLoading}
                    maxLength={500}
                  />
                  <div className={`post-create-input-wrapper ${hasAttachments ? 'has-attachments' : ''}`} ref={inputWrapperRef}>
                    <textarea
                      className="post-create-content"
                      placeholder="Add detail (optional)"
                      value={content}
                      onChange={handleContentChange}
                      onKeyDown={handleKeyDown}
                      ref={textareaRef}
                      onFocus={handleFocus}
                      disabled={currentLoading}
                      maxLength={50000}
                      rows={4}
                    />
                    
                    {/* Visibility FAB Menu - Bottom Left Corner */}
                    <div className="post-visibility-fab-container">
                      <div className="post-visibility-fab-wrapper">
                        <button
                          ref={visibilityFabButtonRef}
                          type="button"
                          className="post-visibility-fab-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsVisibilityFabOpen(prev => !prev);
                          }}
                          disabled={currentLoading}
                          aria-expanded={isVisibilityFabOpen}
                          aria-label="Change post visibility"
                          title={`Visibility: ${visibility === 'public' ? 'Public' : visibility === 'followers' ? 'Followers Only' : 'Private'}`}
                        >
                          <Icon 
                            name={visibility === 'public' ? 'Globe' : visibility === 'followers' ? 'Users' : 'Lock'} 
                            className="post-visibility-fab-icon" 
                          />
                        </button>
                        
                        {/* Visibility FAB Menu */}
                        {isVisibilityFabOpen && (
                          <div
                            ref={visibilityFabMenuRef}
                            className="post-visibility-fab-menu"
                            role="menu"
                            aria-orientation="vertical"
                          >
                            <button
                              type="button"
                              className={`post-visibility-fab-menu-item ${visibility === 'public' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVisibility('public');
                                setIsVisibilityFabOpen(false);
                              }}
                              disabled={currentLoading}
                              role="menuitem"
                            >
                              <Icon name="Globe" className="post-visibility-fab-menu-icon" />
                              <span>Public</span>
                            </button>
                            <button
                              type="button"
                              className={`post-visibility-fab-menu-item ${visibility === 'followers' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVisibility('followers');
                                setIsVisibilityFabOpen(false);
                              }}
                              disabled={currentLoading}
                              role="menuitem"
                            >
                              <Icon name="Users" className="post-visibility-fab-menu-icon" />
                              <span>Followers Only</span>
                            </button>
                            <button
                              type="button"
                              className={`post-visibility-fab-menu-item ${visibility === 'private' ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVisibility('private');
                                setIsVisibilityFabOpen(false);
                              }}
                              disabled={currentLoading}
                              role="menuitem"
                            >
                              <Icon name="Lock" className="post-visibility-fab-menu-icon" />
                              <span>Private</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Attachments Preview Inside Extended Input Box */}
                    {hasAttachments && (
                      <>
                        <div className="content-attachments-divider"></div>
                        <div className="content-attachments-section">
                          <AttachmentDisplay
                            attachments={attachments}
                            onRemove={handleRemoveAttachment}
                            onBiohackClick={(biohack) => {
                              setSelectedBiohack(biohack);
                              setShowBiohackInfo(true);
                            }}
                            mode="preview"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Biotag Suggestion Prompt - Shows biohack or biomarker, prioritized */}
                  {(biohackSuggestions.length > 0 || biomarkerSuggestions.length > 0) && (
                    <div className="biohack-suggestion-prompt">
                      <span className="suggestion-prompt-text">Press tab to insert biotag:</span>
                      {biohackSuggestions.length > 0 ? (
                        <button
                          type="button"
                          className="biohack-tag biohack-tag-suggestion"
                          onClick={() => addBiohackTag(biohackSuggestions[0])}
                          title="Click to add this tag"
                        >
                          {biohackSuggestions[0].name}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="biohack-tag biohack-tag--biomarker biohack-tag-suggestion"
                          onClick={() => addBiomarkerTag(biomarkerSuggestions[0])}
                          title="Click to add this tag"
                        >
                          {biomarkerSuggestions[0].name}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Tagged Biotags (Biohacks and Biomarkers) */}
                  {((attachments.biohacks && attachments.biohacks.length > 0) || 
                    (protocolBiohacks && protocolBiohacks.length > 0) || 
                    (reportBiohacks && reportBiohacks.length > 0) ||
                    (biomarkerTags && biomarkerTags.length > 0) ||
                    (reportBiomarkers && reportBiomarkers.length > 0)) && (
                    <div className="detected-biohacks-tags">
                      {/* Manually entered biohack tags */}
                      {attachments.biohacks && attachments.biohacks.map((biohack) => (
                        <span key={biohack.id} className="biohack-tag">
                          {biohack.name}
                        </span>
                      ))}
                      {/* Biohack tags from attached protocol */}
                      {protocolBiohacks && protocolBiohacks.map((biohack) => (
                        <span key={biohack.id} className="biohack-tag">
                          {biohack.name}
                        </span>
                      ))}
                      {/* Biohack tags from attached protocol report */}
                      {reportBiohacks && reportBiohacks.map((biohack) => (
                        <span key={biohack.id} className="biohack-tag">
                          {biohack.name}
                        </span>
                      ))}
                      {/* Manually entered biomarker tags */}
                      {biomarkerTags && biomarkerTags.filter(b => b && b.name).map((biomarker) => (
                        <span key={biomarker.id} className="biohack-tag biohack-tag--biomarker">
                          {biomarker.name}
                        </span>
                      ))}
                      {/* Biomarker tags from attached protocol report */}
                      {reportBiomarkers && reportBiomarkers.filter(b => b && b.name).map((biomarker) => (
                        <span key={biomarker.id} className="biohack-tag biohack-tag--biomarker">
                          {biomarker.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Health Data Sharing Warning - Below Biotags */}
                  {showHealthDataWarning && (
                    <div className="post-visibility-health-warning-container">
                      <div className="post-visibility-health-warning">
                        You are about to share health data publicly. This cannot be fully undone if others save or copy it.
                      </div>
                    </div>
                  )}

                  {/* Protocol Tab Privacy Mismatch Warning */}
                  {hasProtocolPrivacyMismatch && (
                    <div className="post-visibility-health-warning-container">
                      <div className="post-visibility-health-warning">
                        {protocolTabPrivacy === 'private' && visibility === 'public' && (
                          <>Your protocols tab is private, but you're sharing a protocol on a public post.</>
                        )}
                        {protocolTabPrivacy === 'private' && visibility === 'followers' && (
                          <>Your protocols tab is private, but you're sharing a protocol on a followers-only post.</>
                        )}
                        {protocolTabPrivacy === 'followers_only' && visibility === 'public' && (
                          <>Your protocols tab is followers-only, but you're sharing a protocol on a public post.</>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Error message */}
              {displayError && <div className="post-create-error">{displayError}</div>}
              
              {/* Actions */}
              <div className="post-create-actions">
                <div className="post-create-filters">
                  <button
                    type="button"
                    className="filter-btn"
                    onClick={handleProtocolButtonClick}
                    disabled={currentLoading || !!attachments.protocolReport}
                    title={attachments.protocolReport ? 'Remove report first to add protocols' : 'Add protocol'}
                  >
                    <Icon name="TrendingUp" className="icon--sm" />
                    Protocols
                  </button>
                  
                  <button
                    type="button"
                    className="filter-btn"
                    onClick={() => {
                      setShowProtocolReportSelector(true);
                      setIsExpanded(true);
                    }}
                    disabled={currentLoading || attachments.images.length > 0 || !!attachments.protocol || !!attachments.biomarker}
                    title={
                      attachments.images.length > 0 || attachments.protocol || attachments.biomarker
                        ? 'Remove other attachments first to add a report'
                        : 'Add report (can only be attached alone)'
                    }
                  >
                    <Icon name="FileText" className="icon--sm" />
                    Reports
                  </button>
                  
                  {/* Biohacks attachment button removed */}
                  
                  <button
                    type="button"
                    className="filter-btn"
                    onClick={handleBiomarkerButtonClick}
                    disabled={currentLoading || !!attachments.protocolReport}
                    title={attachments.protocolReport ? 'Remove report first to add biomarkers' : 'Add biomarker'}
                  >
                    <Icon name="TestTube" className="icon--sm" />
                    Biomarkers
                  </button>
                  
                  <button 
                    type="button"
                    className="filter-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={currentLoading || !!attachments.protocolReport}
                    title={attachments.protocolReport ? 'Remove report first to add images' : 'Add images'}
                  >
                    <Icon name="Image" className="icon--sm" />
                    Images
                  </button>
                </div>

                <button
                  type="submit"
                  className="btn btn--primary btn--md post-button"
                  disabled={currentLoading || !title.trim()}
                >
                  {currentLoading ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      </form>
      
      {/* Attachment Selectors */}
      {showProtocolSelector && (
        <ProtocolSelector
          onSelect={handleProtocolSelect}
          onClose={handleProtocolDone}
          selectedProtocol={attachments.protocol}
        />
      )}
      
      {showProtocolReportSelector && (
        <ProtocolReportSelector
          onSelect={handleProtocolReportSelect}
          onClose={handleProtocolReportDone}
          selectedReport={attachments.protocolReport}
        />
      )}
      
      {/* BiohackSelector modal removed */}
      
      {showBiomarkerSelector && (
        <BiomarkerSelector
          onSelect={handleBiomarkerSelect}
          onClose={handleBiomarkerDone}
          selectedBiomarker={attachments.biomarker}
        />
      )}
      
      {showBiohackInfo && selectedBiohack && (
        <BiohackInfoCard 
          biohack={selectedBiohack} 
          onClose={() => setShowBiohackInfo(false)} 
          onRatingSubmitted={() => {
            // Refresh data if needed
            console.log('Rating submitted for biohack in post creation');
          }} 
        />
      )}

      {/* Post Creation Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Post Creation Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for creating this post</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostCreationBox;
